import {SupportedLanguage, TranslationDict} from '../types';

/**
 * The primary (English) field can display a fallback borrowed from another
 * language when the backend has no English translation (see getLocalizedText).
 * Treat that display text as real English only once the user actually edits
 * the field — otherwise keep whatever is truly stored for English, so a
 * missing translation doesn't get silently duplicated into `en` on save.
 */
export const resolveEnglishText = (
  translations: TranslationDict,
  liveValue: string,
  isFieldDirty: boolean,
): string =>
  (isFieldDirty ? liveValue : translations[SupportedLanguage.English]) ?? '';

export const createEmptyTranslationDict = () => {
  // eslint-disable-next-line unicorn/no-array-reduce
  return Object.values(SupportedLanguage).reduce((accum, lang) => {
    accum[lang] = '';
    return accum;
  }, {} as TranslationDict);
};

export const createTranslationDict = (value: unknown): TranslationDict => {
  const translations = createEmptyTranslationDict();

  if (typeof value === 'string') {
    translations[SupportedLanguage.English] = value;
    return translations;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return translations;
  }

  const transDict = value as Record<string, unknown>;

  for (const lang of Object.values(SupportedLanguage)) {
    const translatedValue = transDict[lang];

    if (typeof translatedValue === 'string') {
      translations[lang] = translatedValue;
    }
  }

  return translations;
};
